// Datos y tipos que el wizard les pasa a las piezas de Promociones montadas en sus lugares.
// Puro, sin React.
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { SaleBonus } from './accountBonuses';
import { anchorLineOf } from './buildSale';
import type { ReservedLine, WizardSession } from './types';

/** Relee la sesión del wizard; la pieza lo llama después de su PUT o DELETE. */
export interface SessionRefreshContext {
    refreshSession: () => Promise<void>;
}

/** Pinta «Bonos a tu cuenta» con los Wallet de la venta y los bonos de crédito del equipo. */
export type RenderAccountBonuses = (saleBonuses: readonly SaleBonus[]) => ReactNode;

/** Qué cambió en la venta: los extras o la forma de pago. */
export interface SaleChange {
    kind: 'lines' | 'paymentMethod';
    /** Con `paymentMethod`: si la venta va con crédito SureKeep. */
    sureKeep?: boolean;
}

/** Lo que la pieza soltó al cambiar la forma de pago, para avisarlo en pantalla. */
export interface SaleChangeOutcome {
    notices: string[];
}

/**
 * Aviso a Promociones cuando una mutación de la venta terminó bien. Con un cambio de forma de pago,
 * la promesa termina cuando la pieza soltó lo que ya no aplica y trae sus avisos.
 */
export type OnSaleChanged = (change: SaleChange) => Promise<SaleChangeOutcome | undefined> | undefined;

/** La pieza no pudo dejar la venta lista para el cambio; `message` es el aviso al vendedor. */
export class SaleChangeBlockedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SaleChangeBlockedError';
    }
}

/** Regalo vivo ligado a la ancla. */
export interface AnchorGiftLine {
    lineId: string;
    sku: string;
    promotionId?: string;
    promotionVersion?: number;
    promotionRevalidationPending?: boolean;
}

/** Burbuja de un beneficio en la fila de la tarjeta. */
export interface BenefitBubble {
    key: string;
    label: string;
    icon: LucideIcon;
    /** Texto completo para el tooltip. */
    title?: string;
    /** Color del grupo de la promo; sin tono es verde (descuento). */
    tone?: 'coupon' | 'credit' | 'gift' | 'benefit';
}

/** Lo que Promociones le da a una tarjeta: burbujas verdes y el precio con descuento. */
export interface CardSaleBenefits {
    bubbles: BenefitBubble[];
    /** `null` si ninguna promo baja el precio del equipo. */
    discountedPriceCents: number | null;
    /** Precio de contado (sin promos solo SureKeep); solo viene si difiere del de arriba. */
    cashPriceCents?: number;
}

/** `open` sin cotizar, `quoted` con crédito vivo que se descarta al cambiar, `charged` ya cobrada. */
export type SaleQuoteState = 'open' | 'quoted' | 'charged';

/** Con folio o cobro el servidor ya rechaza cambios de promo (DISCOUNT_AFTER_QUOTE). */
export function saleQuoteStateOf(session: WizardSession): SaleQuoteState {
    if (Boolean(session.folio) || session.payment != null) return 'charged';
    return session.loan?.creditId ? 'quoted' : 'open';
}

/** Regalos vivos (sin RELEASING) ligados a la ancla actual. */
export function anchorGiftLinesOf(session: WizardSession): AnchorGiftLine[] {
    const anchorId = anchorLineOf(session)?.lineId;
    if (anchorId === undefined) return [];
    return (session.reservedLines ?? [])
        .filter((l) => l.status !== 'RELEASING' && l.role === 'GIFT' && l.linkedLineId === anchorId)
        .map(toAnchorGiftLine);
}

function toAnchorGiftLine(l: ReservedLine): AnchorGiftLine {
    return {
        lineId: l.lineId,
        sku: l.sku,
        ...(l.promotionId !== undefined && { promotionId: l.promotionId }),
        ...(l.promotionVersion !== undefined && { promotionVersion: l.promotionVersion }),
        ...(l.promotionRevalidationPending === true && { promotionRevalidationPending: true }),
    };
}
